import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { BanknoteIcon, CalendarIcon, LockIcon, UserIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
type ProcessingStatus = 'idle' | 'processing' | 'success' | 'error';
export function VirtualTerminal() {
  const {
    t
  } = useTranslation('payments');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');
  const [reference, setReference] = useState('');
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').substring(0, 19);
  };
  const formatExpiryDate = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 2) return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCvv(e.target.value.replace(/\D/g, '').substring(0, 4));
  };
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    if (value.split('.').length > 2) return;
    setAmount(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !amount) return;
    setProcessingStatus('processing');
    const newReference = `REF-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    setReference(newReference);
    setTimeout(() => {
      setProcessingStatus(Math.random() < 0.8 ? 'success' : 'error');
    }, 2000);
  };
  const resetForm = () => {
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setAmount('');
    setDescription('');
    setProcessingStatus('idle');
    setReference('');
  };
  const closeModal = () => {
    if (processingStatus === 'success') resetForm();else setProcessingStatus('idle');
  };
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const isFormValid = cardNumber && expiryDate && cvv && cardholderName && amount && consentConfirmed;
  return <div className="max-w-2xl mx-auto" data-id="element-9422" data-mp="1nk510vlrliix">
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-9423" data-mp="1io6ivpr14wqy">
        {/* ─── PAYMENT DETAILS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-9424" data-mp="1xckk5w8ssd5e">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-9425" data-mp="1sd7vr72a2y0p">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-9426" data-mp="1sdztoojcl7u5">
              <BanknoteIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-9427" data-mp="14oez18du0m51" />
              {t('components.FixedLinksPayment.payment_details')}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-9428" data-mp="1eljlzafi4lmw">
              {t('pages.VirtualTerminal.process_a_mail_order_telephone_order')}
            </p>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-9429" data-mp="19lf6nnqbxwy9">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-9430" data-mp="2pzx6c5b1qiy6">
              {t('components.BulkPaymentPreview.amount')}{' '}
              <span className="text-red-400" data-id="element-9431" data-mp="ul1hsh0mrry1">*</span>
            </label>
            <div className="relative" data-id="element-9432" data-mp="umurk56jisa9">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9433" data-mp="zjct6w6kqaid">
                <span className="text-sm font-medium text-gray-500" data-id="element-9434" data-mp="13kybwy5shj0h">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-9435" data-mp="1djnihus8jh1f" />
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-9436" data-mp="1n9yzahx0pkpq">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-9437" data-mp="1rb0zb0yre0sr">
              {t('components.BulkPaymentPreview.description')}{' '}
              <span className="normal-case text-gray-400" data-id="element-9438" data-mp="1wbyf56waqir3">
                {t('components.RecurringPayment.optional')}
              </span>
            </label>
            <input type="text" className={inputClass} placeholder={t('pages.VirtualTerminal.attrs.eg_product_purchase')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-9439" data-mp="2bdf0a8yswyky" />
          </div>
        </div>

        {/* ─── CARD INFORMATION ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-9440" data-mp="1xfgrrk75vwdd">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-9441" data-mp="2u2bnjsvs6qto">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-9442" data-mp="2p4tjafspkzs0">
              <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-9443" data-mp="2kdtp3iaz4vt7" />
              {t('pages.VirtualTerminal.card_information')}
            </h2>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-9444" data-mp="2gbzrxjzzm9xs">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-9445" data-mp="3cue4l5cc494g">
              {t('pages.VirtualTerminal.card_number')}{' '}
              <span className="text-red-400" data-id="element-9446" data-mp="165vpo2yc2g5u">*</span>
            </label>
            <div className="relative" data-id="element-9447" data-mp="166nl9drxkdw4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9448" data-mp="1b4p35vdme760">
                <CreditCardIcon size={14} className="text-gray-400" data-id="element-9449" data-mp="wgc3anbew468" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.xxxx_xxxx_xxxx_xxxx')} value={cardNumber} onChange={handleCardNumberChange} data-id="element-9450" data-mp="11f5jwolzwbam" />
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 grid grid-cols-2 gap-4" data-id="element-9451" data-mp="dirfzkoapdnq">
            <div data-id="element-9452" data-mp="ihckhwakyjhz">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-9453" data-mp="igt7mtzselat">
                {t('components.FixedLinksPayment.expiry_date')}{' '}
                <span className="text-red-400" data-id="element-9454" data-mp="2ct9cbqcxprdk">*</span>
              </label>
              <div className="relative" data-id="element-9455" data-mp="16fme9wwjhv5">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9456" data-mp="17iayb6r587w">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-9457" data-mp="3seurqp3q3jna" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.mmyy')} value={expiryDate} onChange={handleExpiryDateChange} data-id="element-9458" data-mp="aycz5l3k6hvz" />
              </div>
            </div>
            <div data-id="element-9459" data-mp="fvlg5icz0fvn">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-9460" data-mp="fy7t5ximrnl9">
                {t('pages.VirtualTerminal.cvv_cvc')}{' '}
                <span className="text-red-400" data-id="element-9461" data-mp="ox2x12y7clle">*</span>
              </label>
              <div className="relative" data-id="element-9462" data-mp="2vm7tum0tefln">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9463" data-mp="2vp3pg3p6ws6x">
                  <LockIcon size={14} className="text-gray-400" data-id="element-9464" data-mp="2qq0x9jub6ggd" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.xxx')} value={cvv} onChange={handleCvvChange} data-id="element-9465" data-mp="24uh2lt8id5os" />
              </div>
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-9466" data-mp="2jlhj8ljes5w0">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-9467" data-mp="1lwobrpb5sm7r">
              {t('pages.VirtualTerminal.cardholder_name')}{' '}
              <span className="text-red-400" data-id="element-9468" data-mp="1vu2deisfb6g0">*</span>
            </label>
            <div className="relative" data-id="element-9469" data-mp="1vsrbypmgaas2">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9470" data-mp="20svvkoinnlmi">
                <UserIcon size={14} className="text-gray-400" data-id="element-9471" data-mp="379hiyayp76mq" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder={t('pages.VirtualTerminal.attrs.name_as_it_appears_on_card')} value={cardholderName} onChange={e => setCardholderName(e.target.value)} data-id="element-9472" data-mp="3c8apfjfkhxde" />
            </div>
          </div>
        </div>

        {/* ─── SECURITY NOTE ─── */}
        <div className="flex items-center gap-2 px-1 text-xs text-gray-400" data-id="element-9473" data-mp="lwujdz232s2p">
          <LockIcon size={12} className="flex-shrink-0" data-id="element-9474" data-mp="gwq3ob9021dj" />
          <span data-id="element-9475" data-mp="gyay80vbdkbe">
            {t('pages.VirtualTerminal.card_information_is_securely_processed_and')}
          </span>
        </div>

        {/* ─── CARDHOLDER CONSENT ─── */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-id="element-9476" data-mp="7wjpen5h8xdb">
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-9477" data-mp="2yrqh96f0azm">
            <input type="checkbox" checked={consentConfirmed} onChange={e => setConsentConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A] focus:ring-offset-0 cursor-pointer flex-shrink-0" data-id="element-9478" data-mp="22akrdgwbq0hm" />
            <span className="text-sm text-amber-900 leading-relaxed" data-id="element-9479" data-mp="1xdcidlklne5q">
              I confirm that I have obtained the cardholder's consent to process
              this card payment for the amount specified above.
            </span>
          </label>
        </div>

        {/* ─── SUBMIT ─── */}
        <div className="flex justify-end" data-id="element-9480" data-mp="1jniryytg4ltw">
          <button type="submit" disabled={!isFormValid} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${isFormValid ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-9481" data-mp="1enmmsw90iocr">
            {t('pages.VirtualTerminal.process_payment')}
          </button>
        </div>
      </form>

      {/* ─── RESULT MODAL ─── */}
      {processingStatus !== 'idle' && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-9482" data-mp="lt80fr7dvm7f">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-9483" data-mp="bvk44wu4q6n5">
              {/* Processing */}
              {processingStatus === 'processing' && <div className="p-8 flex flex-col items-center" data-id="element-9484" data-mp="14jpnpg9g5ple">
                  <div className="w-14 h-14 rounded-full bg-[#E2231A]/10 flex items-center justify-center mb-4" data-id="element-9485" data-mp="14kh95y9by6xy">
                    <LoaderIcon size={24} className="text-[#9F140F] animate-spin" data-id="element-9486" data-mp="19gg5q94tav9g" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-9487" data-mp="zj1oi7b2e2g3">
                    {t('pages.VirtualTerminal.processing_payment')}
                  </h3>
                  <p className="text-sm text-gray-500 text-center" data-id="element-9488" data-mp="3kb3tvlvld6xg">
                    {t('pages.VirtualTerminal.please_wait_while_we_process_the')}
                  </p>
                </div>}

              {/* Success */}
              {processingStatus === 'success' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-9489" data-mp="1gzyuyx4nfkqh">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-9490" data-mp="1c1e2jjvn25cl">
                      {t('pages.VirtualTerminal.payment_successful')}
                    </h3>
                    <button onClick={closeModal} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-9491" data-mp="130z9fycfnlyj">
                      <XIcon size={18} aria-hidden="true" data-id="element-9492" data-mp="t6pdk6oa85j2" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-9493" data-mp="2nidw0itq2q87">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-4" data-id="element-9494" data-mp="2nj6tsuape5e7">
                      <CheckCircleIcon size={28} className="text-green-600" data-id="element-9495" data-mp="2uztweq457sm3" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-5" data-id="element-9496" data-mp="2zywufqlpgykk">
                      {t('pages.VirtualTerminal.the_transaction_has_been_approved')}
                    </p>

                    {/* Transaction details */}
                    <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden mb-5" data-id="element-9497" data-mp="2c4vf3io9r9jp">
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-9498" data-mp="2h480dyzhs8k6">
                        <span className="text-xs text-gray-500" data-id="element-9499" data-mp="2m3kxdck2uw0h">
                          {t('components.BulkPaymentPreview.amount')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-9500" data-mp="1ahgjsemh30v">
                          {formatCurrencyAmount(parseFloat(amount || '0'), {
                    fractionDigits: 2
                  })}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-9501" data-mp="3w0jbx2n00lmc">
                        <span className="text-xs text-gray-500" data-id="element-9502" data-mp="dv6brfx4rc9d">
                          {t('components.BulkPaymentPreview.reference')}
                        </span>
                        <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-9503" data-mp="mthyv7ysfrx2">
                          {reference}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-9504" data-mp="wkme88sdlesf">
                        <span className="text-xs text-gray-500" data-id="element-9505" data-mp="rkq8a404bda8">
                          {t('components.CardOnFilePayment.card')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-9506" data-mp="16fweos34le43">
                          •••• {cardNumber.slice(-4)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5" data-id="element-9507" data-mp="1l2v3sgeifwfn">
                        <span className="text-xs text-gray-500" data-id="element-9508" data-mp="1p5rsmoun8os1">
                          {t('pages.VirtualTerminal.date')}
                        </span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-9509" data-mp="1g53frxgzkugq">
                          {new Date().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-9510" data-mp="168i977pbjbas">
                      {t('pages.VirtualTerminal.new_transaction')}
                    </button>
                  </div>
                </>}

              {/* Error */}
              {processingStatus === 'error' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-9511" data-mp="4dnlayqibc38">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-9512" data-mp="3vqhxu8wezdc3">
                      {t('pages.VirtualTerminal.transaction_failed')}
                    </h3>
                    <button onClick={closeModal} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-9513" data-mp="3ltlzgw08c7l9">
                      <XIcon size={18} aria-hidden="true" data-id="element-9514" data-mp="idev0n9zq6bf" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-9515" data-mp="dbz8eqs5zrqx">
                    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4" data-id="element-9516" data-mp="8epd4ui3glv6">
                      <XCircleIcon size={28} className="text-red-600" data-id="element-9517" data-mp="2y3rm8dc00h0h" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-4" data-id="element-9518" data-mp="2t45903w670ro">
                      {t('pages.VirtualTerminal.the_payment_could_not_be_processed')}
                    </p>

                    <div className="w-full bg-red-50 border border-red-100 rounded-lg p-3 mb-5" data-id="element-9519" data-mp="1dm0edilneh3u">
                      <p className="text-xs text-red-700" data-id="element-9520" data-mp="1iiz13g2zfls0">
                        {t('pages.VirtualTerminal.transaction_declined_by_issuing_bank_please')}
                      </p>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-9521" data-mp="1wb6y98y6her8">
                      {t('pages.VirtualTerminal.try_again')}
                    </button>
                  </div>
                </>}
            </div>
          </div>, document.body)}
    </div>;
}